const API_ERROR_CODE_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: "入力内容に誤りがあります",
  UNAUTHORIZED: "ログインが必要です",
  FORBIDDEN: "この操作を実行する権限がありません",
  NOT_FOUND: "対象が見つかりません",
  INTERNAL: "サーバーで問題が発生しました",
  WRONG_CREDENTIALS: "ログインIDまたはパスワードが違います",
  TOTP_REQUIRED: "認証コードを入力してください",
  TOTP_INVALID: "認証コードが正しくありません",
  REFRESH_INVALID: "セッションが無効です。再ログインしてください",
  REFRESH_EXPIRED: "セッションの有効期限が切れました。再ログインしてください",
  PASSWORD_SETUP_NOT_ALLOWED: "このユーザーは初回パスワード設定の対象ではありません",
  PASSWORD_SETUP_TOKEN_INVALID: "設定リンクが正しくありません",
  PASSWORD_SETUP_TOKEN_EXPIRED: "設定リンクの有効期限が切れています",
  USER_NOT_FOUND: "ユーザーが見つかりません",
  LP_NOT_FOUND: "LP が見つかりません",
  LP_CATEGORY_NOT_FOUND: "LP カテゴリが見つかりません",
  LP_CTA_GROUP_CONFLICT: "CTA グループの設定に競合があります",
  CTA_NOT_FOUND: "CTA が見つかりません",
  OFFER_NOT_FOUND: "案件が見つかりません",
  OFFER_MISSING_SPARK_FIELDS: "SPARK 同期に必要な案件項目が未入力です",
  MATERIAL_NOT_FOUND: "素材が見つかりません",
  MATERIAL_MISSING_SPARK_FIELDS: "SPARK 同期に必要な素材項目が未入力です",
  MEDIA_ASSET_NOT_FOUND: "ファイルが見つかりません",
  MEDIA_ASSET_FILENAME_CONFLICT: "同名のファイルが既に存在します",
  MEDIA_ASSET_IN_USE: "公開中のNOVAサイトで参照中のLPファイルは削除できません",
  MEDIA_BUCKET_NOT_CONFIGURED: "ファイル保存先が設定されていません",
  NOVA_SITE_NOT_FOUND: "NOVA サイトが見つかりません",
  NOVA_SITE_IN_USE: "公開中のクッション広告は削除できません",
  NOVA_SITE_SPARK_STOP_FAILED:
    "SPARK 側の停止に失敗したため、クッション広告を削除できません",
  NOVA_SITE_SPARK_STOP_NOT_READY:
    "SPARK 側の停止に必要な同期情報が保存されていません",
  NOVA_SITE_CTA_GROUP_NOT_FOUND: "NOVA サイトの CTA グループが見つかりません",
  NOVA_SITE_ITEM_NOT_FOUND: "NOVA サイトの CTA が見つかりません",
  NOVA_SITE_ITEM_CONFLICT: "同一グループ内に同じ CTA は追加できません",
  PROMOTION_GIVEN_NOT_FOUND: "限定公開対象が見つかりません",
};

const KNOWN_MESSAGE_TRANSLATIONS: Record<string, string> = {
  "internal server error": "サーバーで問題が発生しました",
  "file is required": "ファイルを選択してください",
  "file size must be 25MiB or less": "ファイルサイズは 25MiB 以下にしてください",
  "only image and video files are allowed": "画像または動画ファイルのみアップロードできます",
  "media bucket is not configured": "ファイル保存先が設定されていません",
  "media asset filename already exists": "同名のファイルが既に存在します",
  "namespace must contain only alphanumerics, hyphens, and underscores":
    "namespace は半角英数字、ハイフン、アンダースコアのみ使用できます",
  "Failed to fetch": "サーバーに接続できませんでした",
  "NetworkError when attempting to fetch resource.":
    "サーバーに接続できませんでした",
  "Load failed": "読み込みに失敗しました",
  "form not ready": "フォームの準備が完了していません",
  "Offer creation failed": "案件の作成に失敗しました",
  "NovaSite creation failed": "NOVA サイトの作成に失敗しました",
  "#root not found": "アプリケーションの起動に失敗しました",
  "useAuthContext must be used within AuthProvider":
    "認証状態の初期化に失敗しました",
  "useFormField should be used within <FormField>":
    "フォーム項目の初期化に失敗しました",
  "useFormField should be used within <FormItem>":
    "フォーム項目の初期化に失敗しました",
  "password must be at least 8 characters":
    "パスワードは8文字以上で入力してください",
  "displayName is required": "表示名を入力してください",
  "TOTP code is invalid": "認証コードが正しくありません",
  "failed to generate unique login ID": "ログインIDの発行に失敗しました",
  "failed to generate unique CTA ID": "CTA ID の発行に失敗しました",
  "invalid access token": "セッションが無効です。再ログインしてください",
  "invalid refresh token": "セッションが無効です。再ログインしてください",
  "NOVA site SPARK sync did not return required IDs":
    "SPARK 同期結果に必要な ID が含まれていません",
  "Failed to upsert SPARK promotion reference":
    "SPARK 案件参照の保存に失敗しました",
  "Failed to upsert SPARK promotion item reference":
    "SPARK 素材参照の保存に失敗しました",
  "NOVA_PUBLIC_ORIGIN must be a valid absolute URL.":
    "公開 URL 設定が正しくありません",
  "NOVA_PUBLIC_ORIGIN must be an http or https URL.":
    "公開 URL 設定は http または https にしてください",
  "offerId or promotionId is required": "案件 ID または SPARK 案件 ID が必要です",
};

const STATUS_MESSAGES: Record<number, string> = {
  400: "入力内容に誤りがあります",
  401: "ログインが必要です",
  403: "この操作を実行する権限がありません",
  404: "対象が見つかりません",
  409: "データが競合しています",
  422: "入力内容に誤りがあります",
  500: "サーバーで問題が発生しました",
};

export function localizeApiErrorMessage(
  status: number,
  code: string,
  message?: string,
): string {
  if (message) {
    const localized = localizePlainErrorMessage(message, "");
    if (localized) return localized;
  }

  return (
    API_ERROR_CODE_MESSAGES[code] ??
    STATUS_MESSAGES[status] ??
    "処理に失敗しました"
  );
}

export function toDisplayErrorMessage(
  error: unknown,
  fallback = "処理に失敗しました",
): string {
  if (typeof error === "string") {
    return localizePlainErrorMessage(error, fallback);
  }
  if (error instanceof Error) {
    return localizePlainErrorMessage(error.message, fallback);
  }
  return fallback;
}

export function formatErrorMessage(
  title: string,
  error: unknown,
  fallback = "詳細を取得できませんでした",
): string {
  const detail = toDisplayErrorMessage(error, "");
  return detail ? `${title}: ${detail}` : `${title}: ${fallback}`;
}

export function localizePlainErrorMessage(
  message: string,
  fallback = "処理に失敗しました",
): string {
  const trimmed = message.trim();
  if (!trimmed) return fallback;

  const direct =
    KNOWN_MESSAGE_TRANSLATIONS[trimmed] ?? API_ERROR_CODE_MESSAGES[trimmed];
  if (direct) return direct;

  const patterned = localizePatternedMessage(trimmed);
  if (patterned) return patterned;

  if (containsJapanese(trimmed)) {
    return hasUntranslatedEnglishPhrase(trimmed)
      ? japaneseSummary(trimmed, fallback)
      : trimmed;
  }
  if (/[A-Za-z]/.test(trimmed)) return fallback;
  return trimmed;
}

function localizePatternedMessage(message: string): string | null {
  const materialMissing = message.match(
    /^Material (.+) cannot be synced to SPARK: missing required fields \[(.+)\]$/,
  );
  if (materialMissing) {
    return `SPARK 同期に必要な素材項目が未入力です: ${materialMissing[2]
      .split(",")
      .map((field) => translateMaterialField(field.trim()))
      .join(", ")}`;
  }

  const ctaExists = message.match(/^CTA already exists in group: (.+)$/);
  if (ctaExists) {
    return `同一グループ内に同じ CTA は追加できません: ${ctaExists[1]}`;
  }

  const ctaGroupDuplicated = message.match(/^CTA group key is duplicated: (.+)$/);
  if (ctaGroupDuplicated) {
    return `CTA グループキーが重複しています: ${ctaGroupDuplicated[1]}`;
  }

  const ctaGroupUsed = message.match(
    /^CTA group is used by NOVA site items: (.+)$/,
  );
  if (ctaGroupUsed) {
    return `NOVA サイトで使用中の CTA グループは削除できません: ${ctaGroupUsed[1]}`;
  }

  const notFound = message.match(
    /^(Cta|Lp|LpCategory|Offer|Material|Media asset|NovaSite|NovaSite CTA group|NovaSite item|User|promotion given) not found: (.+)$/i,
  );
  if (notFound) {
    return `${translateEntityName(notFound[1])}が見つかりません: ${notFound[2]}`;
  }

  const invalidDateTime = message.match(/^invalid datetime: (.+)$/);
  if (invalidDateTime) {
    return `日時の形式が正しくありません: ${invalidDateTime[1]}`;
  }

  if (/^SPARK \/.+\/regist did not return record\.id$/.test(message)) {
    return "SPARK から登録 ID が返されませんでした";
  }

  const sparkNotFound = message.match(/^SPARK .+ not found: (.+)$/);
  if (sparkNotFound) {
    return `SPARK 上の対象が見つかりません: ${sparkNotFound[1]}`;
  }

  return null;
}

function translateMaterialField(field: string): string {
  if (field === "name") return "素材名";
  if (field === "adText") return "広告テキスト";
  return "未入力項目";
}

function translateEntityName(entity: string): string {
  const normalized = entity.toLowerCase();
  if (normalized === "cta") return "CTA";
  if (normalized === "lp") return "LP";
  if (normalized === "lpcategory") return "LP カテゴリ";
  if (normalized === "offer") return "案件";
  if (normalized === "material") return "素材";
  if (normalized === "media asset") return "ファイル";
  if (normalized === "novasite") return "NOVA サイト";
  if (normalized === "novasite cta group") return "NOVA サイトの CTA グループ";
  if (normalized === "novasite item") return "NOVA サイトの CTA";
  if (normalized === "user") return "ユーザー";
  if (normalized === "promotion given") return "限定公開対象";
  return "対象";
}

function containsJapanese(value: string): boolean {
  return /[ぁ-んァ-ン一-龯]/.test(value);
}

function hasUntranslatedEnglishPhrase(value: string): boolean {
  const withoutAllowedTerms = value.replace(
    /\b(SPARK|NOVA|CTA|LP|ID|URL|TOTP|MIME|MiB|KB|MB|B|namespace|http|https|promotion_id)\b/gi,
    "",
  );
  return (
    /(required|failed|invalid|not found|must|should|cannot|already|exists|missing|return|registered|configured|allowed|attempting|fetch|error)/i.test(
      withoutAllowedTerms,
    ) || /[A-Za-z]{3,}\s+[A-Za-z]{2,}/.test(withoutAllowedTerms)
  );
}

function japaneseSummary(message: string, fallback: string): string {
  const prefix = message.split(":")[0]?.trim();
  if (prefix && containsJapanese(prefix) && !hasUntranslatedEnglishPhrase(prefix)) {
    return prefix;
  }
  return fallback;
}
